import React from 'react';
import { TrendingUp } from 'lucide-react';
import { apiClient } from '../../lib/apiClient';
import { REPORT_ACCENT } from './reportTheme';
import { DMA_REGIONS } from '../../data/dmaList';
import { bucketByDaypart, shiftHoursToLocal } from './dayparts';
import { rangeQuery } from './reportRange';

// CTV-445: a plain-English takeaway band at the top of the report. Self-fetches
// the delivery / geo / daypart endpoints and assembles a single sentence with
// the headline facts (impressions, top market, peak daypart, completion rate).
// Self-hides while loading and when there's no delivery yet.

const DMA_NAME_BY_CODE: Record<string, string> = Object.fromEntries(
  DMA_REGIONS.map((d) => [d.code, d.name]),
);

interface InsightsBandProps {
  submissionId: string;
  accentColor?: string;
  from?: string | null;
  to?: string | null;
  // Live (still delivering) → present-tense "So far, your ad has delivered…".
  live?: boolean;
}

interface Insights {
  impressions: number;
  completes: number;
  topMarket: string | null;
  peakDaypart: string | null;
}

const InsightsBand: React.FC<InsightsBandProps> = ({ submissionId, accentColor, from, to, live }) => {
  const accent = accentColor || REPORT_ACCENT;
  const [data, setData] = React.useState<Insights | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    // Keep the previous reading visible while the new range loads (no flash to
    // empty); `loading` just dims it until the fresh data lands.
    setLoading(true);
    const q = rangeQuery({ from: from ?? null, to: to ?? null });
    Promise.allSettled([
      apiClient.get<{ daily: Array<{ plays: number; completes?: number }> }>(`/submissions/${submissionId}/delivery-daily${q}`),
      apiClient.get<{ byDma: Array<{ dma: string; name?: string | null; impressions: number }> }>(`/submissions/${submissionId}/geo${q}`),
      apiClient.get<{ byHour: Array<{ hour: number; impressions: number }>; offsetMinutes?: number | null }>(`/submissions/${submissionId}/daypart${q}`),
    ]).then(([d, g, dp]) => {
      if (cancelled) return;
      const daily = d.status === 'fulfilled' ? d.value.daily ?? [] : [];
      const impressions = daily.reduce((a, x) => a + (x.plays ?? 0), 0);
      const completes = daily.reduce((a, x) => a + (x.completes ?? 0), 0);

      const byDma = g.status === 'fulfilled' ? g.value.byDma ?? [] : [];
      const top = byDma[0]; // endpoint returns these sorted desc
      // Prefer the census-derived market name (matches the map); fall back to
      // the static list, then the raw code.
      const topMarket = top ? top.name || DMA_NAME_BY_CODE[top.dma] || top.dma : null;

      const byHour = dp.status === 'fulfilled' ? dp.value.byHour ?? [] : [];
      const offsetMinutes = dp.status === 'fulfilled' ? dp.value.offsetMinutes : null;
      const buckets = bucketByDaypart(shiftHoursToLocal(byHour, offsetMinutes));
      const peak = buckets.reduce(
        (m, b) => (b.impressions > m.impressions ? b : m),
        buckets[0] ?? { label: '', impressions: 0 },
      );
      // Use the daypart's display name ("Primetime") for the takeaway sentence.
      const peakDaypart = peak && peak.impressions > 0 ? peak.label : null;

      setData({ impressions, completes, topMarket, peakDaypart });
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [submissionId, from, to]);

  if (!data || data.impressions === 0) return null;

  const rate =
    data.completes > 0 && data.impressions > 0
      ? Math.min(100, Math.round((data.completes / data.impressions) * 100))
      : null;
  const strong = (text: string) => (
    <span className="font-semibold" style={{ color: accent }}>
      {text}
    </span>
  );

  return (
    <div
      className={`rounded-2xl border px-5 py-4 flex items-start gap-3 transition-opacity duration-300 ${loading ? 'opacity-60' : 'opacity-100'}`}
      style={{ backgroundColor: `${accent}12`, borderColor: `${accent}33` }}
    >
      <TrendingUp className="w-5 h-5 mt-0.5 shrink-0" style={{ color: accent }} />
      <p className="text-[14px] text-slate-700 leading-relaxed">
        {live ? 'So far, your ad has delivered ' : 'Your ad delivered '}
        {strong(`${data.impressions.toLocaleString('en-US')} plays`)}
        {data.topMarket && <> across the <span className="font-semibold text-slate-900">{data.topMarket}</span> area</>}
        {data.peakDaypart && (
          <>
            , peaking in the{' '}
            <span className="font-semibold text-slate-900">{data.peakDaypart}</span> window
          </>
        )}
        {rate != null && <>, with a {strong(`${rate}% completion rate`)}</>}.
      </p>
    </div>
  );
};

export default InsightsBand;
